import { useEffect, useMemo, useState } from 'react';
import { Icon } from '@/components';
import type { RosterEntry } from '@/types/domain';
import { aggregate, baseline, computePreSuperlatives, computeSuperlatives, entryKey, finishers, preFinishers } from './scoreboard';
import { AvatarTile } from './AvatarTile';
import { SuperlativeStrip } from './SuperlativeStrip';
import { confettiVolley } from './confetti';

/**
 * Act 1 — the wall of avatars (no names). Two modes:
 *  - post: the celebration. Pass glows gold, confetti fires, superlatives cheer.
 *  - pre:  the "starting line". Neutral baseline tiles, no pass/fail, no confetti —
 *          it sets up the post-test payoff ("look how far we came").
 */
export function HeroWall({ roster, titleTh, onReview, mode }: { roster: RosterEntry[]; titleTh: string; onReview: () => void; mode: 'pre' | 'post' }) {
  const [replayKey, setReplayKey] = useState(0);
  const [cascade, setCascade] = useState(true);

  const done = useMemo(() => (mode === 'pre' ? preFinishers(roster) : finishers(roster)), [roster, mode]);
  const agg = useMemo(() => aggregate(roster), [roster]);
  const pre = useMemo(() => baseline(roster), [roster]);
  const sups = useMemo(() => (mode === 'pre' ? computePreSuperlatives(roster) : computeSuperlatives(roster)), [roster, mode]);

  const size = done.length > 48 ? 62 : done.length > 28 ? 78 : 96;

  // Opening cascade; the gold confetti volley is a post-test celebration only.
  useEffect(() => {
    setCascade(true);
    const stagger = Math.min(done.length * 0.06, 2.4) * 1000 + 600;
    const volley = mode === 'post' ? window.setTimeout(() => confettiVolley(), Math.min(stagger, 1600)) : undefined;
    const settle = window.setTimeout(() => setCascade(false), stagger + 400);
    return () => { if (volley) window.clearTimeout(volley); window.clearTimeout(settle); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replayKey, mode]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 16 }}>
      {/* header + aggregate */}
      <div style={{ textAlign: 'center', flex: 'none' }}>
        <div className="th" style={{ fontSize: 30, fontWeight: 800, lineHeight: 1.15 }}>{titleTh}</div>
        {mode === 'post' ? (
          <div style={{ display: 'flex', gap: 26, justifyContent: 'center', marginTop: 10, fontSize: 17, color: 'var(--ink-2)' }}>
            <span><strong style={{ fontSize: 23 }}>{agg.finished}</strong>/{agg.total} finished</span>
            <span style={{ color: 'var(--sage-deep)' }}><strong style={{ fontSize: 23 }}>{agg.passRate}%</strong> passed</span>
            <span>avg <strong style={{ fontSize: 23 }}>{agg.avg}</strong> ⭐</span>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 22, justifyContent: 'center', alignItems: 'baseline', marginTop: 10, fontSize: 17, color: 'var(--ink-2)', flexWrap: 'wrap' }}>
            <span><strong style={{ fontSize: 23 }}>{pre.done}</strong>/{pre.total} took the pre-test</span>
            <span>avg <strong style={{ fontSize: 23 }}>{pre.avg}</strong> 🌱</span>
            <span className="th" style={{ color: 'var(--ink-3)', fontSize: 15 }}>จุดเริ่มต้นของเรา — เดี๋ยวเราไปด้วยกัน</span>
          </div>
        )}
      </div>

      {/* the wall */}
      <div
        key={replayKey}
        style={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: 10, alignContent: 'flex-start', justifyContent: 'center', padding: '6px 4px' }}
      >
        {done.length === 0 ? (
          <div className="th" style={{ alignSelf: 'center', color: 'var(--ink-3)', fontSize: 17 }}>
            {mode === 'pre' ? 'ยังไม่มีใครทำแบบทดสอบก่อนเรียน ✨' : 'ยังไม่มีผลสอบ — รอผู้เรียนส่งแบบทดสอบ ✨'}
          </div>
        ) : (
          done.map((r, i) => <AvatarTile key={entryKey(r)} entry={r} size={size} index={i} cascade={cascade} mode={mode} />)
        )}
      </div>

      {/* superlatives + actions */}
      <div style={{ flex: 'none', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <SuperlativeStrip items={sups} />
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
          <button className="btn btn-ghost" onClick={() => setReplayKey((k) => k + 1)}><Icon name="play" size={15} /> Replay</button>
          <button className="btn btn-primary btn-lg" onClick={onReview}>Review most-missed <Icon name="arrowR" size={16} /></button>
        </div>
      </div>
    </div>
  );
}
